import Cookies from 'js-cookie';
import { logger } from 'Utils/Logger';
import { getCookieDomain, getCookieName, getHostname } from './AuthStore';
import { getAppConfig } from 'State/store';
import { FIVE_YEARS } from './Constants';

const NATSSO_TOKEN_NAME = 'NATSSO';
const NATSSO_DOMAIN_NAME = '.foxnews.com';
export const OLD_COOKIE_NAME = 'FOXSSOACCT';

class CookieManager {
  exchangingPromise;

  constructor() {
    this.exchangingPromise = null;
  }

  async set(options) {
    const { name, value: inputValue, expiration } = options;
    const value = name === getCookieName() ? decodeURIComponent(inputValue) : inputValue;

    localStorage.setItem(name, value);

    // Store NATSSO cookie for backward compatibility
    if (name === getCookieName()) {
      const cookieValue = await this.get(name);
      const { accessToken, refreshToken, tokenExpiration, profileId } = JSON.parse(cookieValue || '{}');

      const newNatSSOCookie = JSON.stringify({ profileId, accessToken, refreshToken, tokenExpiration });

      Cookies.set(NATSSO_TOKEN_NAME, newNatSSOCookie, {
        domain: getHostname().includes('local') ? null : NATSSO_DOMAIN_NAME,
        expires: expiration,
      });
    }
  }

  async get(name) {
    const cookieValue = localStorage.getItem(name);
    if (name !== getCookieName()) {
      return cookieValue;
    }

    // Migrate deprecated cookie values to the new cookie
    const oldCookieValue = Cookies.get(OLD_COOKIE_NAME);
    if (oldCookieValue) {
      if (cookieValue) {
        Cookies.remove(OLD_COOKIE_NAME);
      } else {
        return await this.tokenExchangeCustom();
      }
    }

    return cookieValue;
  }

  async tokenExchangeCustom() {
    if (this.exchangingPromise) {
      return await this.exchangingPromise;
    }

    const { identityApiBaseUrl, accountApiKey, authClientId: clientId } = getAppConfig().defaultConfig;

    this.exchangingPromise = (async () => {
      try {
        const oldCookieValue = Cookies.get(OLD_COOKIE_NAME);
        const { accessToken, refreshToken, ...oldCookieFields } = JSON.parse(oldCookieValue || '{}');

        const response = await fetch(`${identityApiBaseUrl}/account/token/exchange/v3`, {
          method: 'POST',
          headers: {
            'X-Api-Key': accountApiKey,
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            clientId,
            refreshToken,
          }),
        });

        if (!response.ok) {
          throw new Error('Token exchange failed');
        }

        const newToken = await response.json();

        await this.set({
          name: getCookieName(),
          value: JSON.stringify({
            ...oldCookieFields,
            tokenExpiration: newToken.tokenExpiration,
            accessToken: newToken.accessToken,
            refreshToken: newToken.refreshToken,
            idToken: newToken.idToken,
          }),
          domain: getCookieDomain(),
          expiration: new Date(newToken.tokenExpiration + FIVE_YEARS),
        });
      } catch (error) {
        console.log(error);
        // Failed to exchange the old token
        logger.apiError('Token exchange failed during cookie migration', null, error);
      }
      Cookies.remove(OLD_COOKIE_NAME);
      Cookies.remove(OLD_COOKIE_NAME, { domain: getCookieDomain() });

      return localStorage.getItem(getCookieName());
    })();

    return await this.exchangingPromise;
  }

  async has(name) {
    const hasNewCookie = Boolean(localStorage.getItem(name));
    if (name !== getCookieName() || hasNewCookie) {
      return Promise.resolve(hasNewCookie);
    }

    // If it's the auth cookie, can say the cookie exists if the old cookie does, because during the
    // `get` function it'll be migrated
    if (Cookies.get(OLD_COOKIE_NAME)) {
      return true;
    }

    return hasNewCookie;
  }

  delete(options) {
    const { name } = options;

    localStorage.removeItem(name);

    if (name === getCookieName()) {
      Cookies.remove(NATSSO_TOKEN_NAME, { domain: NATSSO_DOMAIN_NAME });
      Cookies.remove(NATSSO_TOKEN_NAME);
    }
  }
}

let cookieManagerInstance;

export const cookieManager = () => {
  if (!cookieManagerInstance) {
    cookieManagerInstance = new CookieManager();
  }

  return cookieManagerInstance;
};
